import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './lib/auth'
import { LocaleProvider } from './lib/i18n'
import Splash from './routes/public/Splash'
import Selector from './routes/public/Selector'
import PackSurface from './routes/public/PackSurface'
import Company from './routes/public/Company'
import Wallet from './routes/public/Wallet'
import Accounts from './routes/admin/Accounts'
import Transcripts from './routes/admin/Transcripts'
import DeskHarness from './routes/dev/DeskHarness'
import MessagingHarness from './routes/dev/MessagingHarness'
import MobileHarness from './routes/dev/MobileHarness'
import ObserverHarness from './routes/dev/ObserverHarness'
import DoorChooser from './routes/public/DoorChooser'
import { findScenario } from './content/scenarios'

/**
 * The whole site.
 *
 *   /            the front door — one address, two states:
 *                  anonymous → the way in                    (PUBLIC)
 *                  signed in → the shelf
 *   /p/:packId   a pack, in whatever grammar it declares
 *   /c/:company  a company's listings — a page of Chats, from the CHATS shelf
 *   /admin       the accounts list (admin only; enforced server-side)
 *
 * ⚠ `/c/` IS A DESTINATION AND NEVER A GATE. Every pack it lists keeps its own `/p/` address and
 * opens straight into the thread; the company page is a second, longer way in. See
 * `content/companies.ts`.
 *
 * THERE IS NO LONGER A THIRD STATE HERE. An account that has not been let in to
 * play is not held on a dead-end page: it gets the shelf and the pack pages like
 * anyone else, and the limit is stated at the door it applies to (see
 * PackSurface / DoorChooser). The gate is on playthroughs, not on accounts —
 * which is why `member` and `approved` route identically and only differ deeper
 * in.
 *
 * Access is decided by the SERVER: `status` and `isAdmin` arrive from /api/me
 * after it validates the Supabase token. Everything here is presentation.
 */
export default function App() {
  return (
    // OUTSIDE the auth branch and outside the dev harnesses, deliberately: the locale must
    // hold on the splash (before there is an account to hang it on) and in the harnesses
    // (which are how the chrome gets judged in three languages without signing in). See
    // lib/i18n.tsx for why it is stored in localStorage rather than on the account.
    <LocaleProvider>
      <Site />
    </LocaleProvider>
  )
}

function Site() {
  const { status, isAdmin, loading } = useAuth()

  /*
   * Dev harnesses come first, deliberately ahead of the auth branch: they render
   * MOCK data and touch no session, so gating them behind sign-in would only
   * make the [CONTRACT PENDING] surfaces unbuildable. They are registered under
   * import.meta.env.DEV, so they are absent from the production bundle entirely
   * — not hidden, gone (desk-grammar brief §0.5).
   */
  if (import.meta.env.DEV && window.location.pathname.startsWith('/dev/')) {
    return (
      <Routes>
        <Route path="/dev/desk" element={<DeskHarness />} />
        <Route path="/dev/mobile" element={<MobileHarness />} />
        {/* The third house: one being, one thread. Mock data, no session. */}
        <Route path="/dev/messaging" element={<MessagingHarness />} />
        {/* The shelf renders from the static manifest, so it needs no session —
            handy for judging layout without going through sign-in. */}
        <Route path="/dev/selector" element={<Selector />} />
        {/* The company page renders from the static manifest too — no session, no sign-in.
            `:companyId` rather than a fixed one so a second company needs no route. */}
        <Route path="/dev/company/:companyId" element={<Company />} />
        {/* The chooser renders from the manifest alone — no session needed. */}
        <Route
          path="/dev/doors"
          element={<DoorChooser scenario={findScenario('silfurberg')!} />}
        />
        {/*
          The same chooser as an unapproved member sees it: every door inert,
          the limit stated once underneath. Judging this needs a real account in
          exactly the wrong state, which is why it gets a route instead.
        */}
        <Route
          path="/dev/doors-locked"
          element={<DoorChooser scenario={findScenario('silfurberg')!} locked />}
        />
        {/*
          The way in. It renders for signed-OUT callers only, so without this
          route the only way to look at it is to sign out of a working session.
        */}
        <Route path="/dev/splash" element={<Splash />} />
        {/* THE OBSERVER'S FILE (2026-09-16): what the house believes about the person in one session, live.
            Reads a dev-only endpoint; a real session id is the whole input. */}
        <Route path="/dev/observer" element={<ObserverHarness />} />
        <Route path="/dev/observer/:sessionId" element={<ObserverHarness />} />
        <Route path="*" element={<Navigate to="/dev/desk" replace />} />
      </Routes>
    )
  }

  // Don't flash the splash at a signed-in reader while the token is checked.
  if (loading) return null

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="*" element={<Splash />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<Selector />} />
      <Route path="/p/:packId" element={<PackSurface />} />
      <Route path="/c/:companyId" element={<Company />} />
      {/* The statement. Reachable from the shelf and from nowhere inside a sitting — a balance a
          player can reach mid-scene is a meter one tap away. */}
      <Route path="/wallet" element={<Wallet />} />
      {isAdmin && <Route path="/admin" element={<Accounts />} />}
      {/* Reading sittings back. The route is admin-gated here for the UI's sake; the endpoints
          behind it are admin-checked server-side, which is the gate that counts. */}
      {isAdmin && <Route path="/admin/transcripts" element={<Transcripts />} />}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
